import { useState, useCallback } from 'react';

const ONBOARDING_STORAGE_PREFIX = 'wexler-onboarding-seen-';

export type OnboardingDialogId =
  | 'matters-welcome'
  | 'matter-creation'
  | 'matter-overview'
  | 'subsets-list'
  | 'subset-creation';

/**
 * Hook to manage onboarding dialog visibility with localStorage persistence.
 * The dialog will show automatically on first visit to a page and stay closed
 * once the user dismisses it.
 */
export function useOnboardingDialog(dialogId: OnboardingDialogId) {
  const storageKey = `${ONBOARDING_STORAGE_PREFIX}${dialogId}`;

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    // Check if the dialog has been seen before
    try {
      const seen = localStorage.getItem(storageKey);
      return seen !== 'true';
    } catch (error) {
      console.error('Error checking onboarding dialog:', error);
      return false;
    }
  });

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (error) {
      console.error('Error closing onboarding dialog:', error);
    }
  }, [storageKey]);

  const resetDialog = useCallback(() => {
    setIsOpen(true);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error resetting onboarding dialog:', error);
    }
  }, [storageKey]);

  return {
    isOpen,
    closeDialog,
    resetDialog,
  };
}

/**
 * Utility function to reset all onboarding dialogs (useful for testing)
 */
export function resetAllOnboardingDialogs() {
  const dialogIds: OnboardingDialogId[] = [
    'matters-welcome',
    'matter-creation',
    'matter-overview',
    'subsets-list',
    'subset-creation',
  ];

  dialogIds.forEach((id) => {
    localStorage.removeItem(`${ONBOARDING_STORAGE_PREFIX}${id}`);
  });
}
